import React, { useState, useEffect } from "react";
import axios from "axios";
import Header from "@/components/Navbar";
import AdvertBanner from "@/components/AdvertBanner";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, Target, TrendingUp, Clock } from "lucide-react";
import { API } from "@/App";

const COUPON_BG = {
  blue:   "bg-blue-600",
  red:    "bg-red-600",
  green:  "bg-green-600",
  yellow: "bg-yellow-500",
  black:  "bg-gray-900",
  white:  "bg-white text-gray-900 border border-gray-300",
};

const PoolFixturesPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | Draw | Home Win | Away Win

  useEffect(() => {
    axios.get(`${API}/pool/fixtures-forecast`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => { setData(null); setLoading(false); });
  }, []);

  const fixtures = data?.fixtures || [];
  const filtered = filter === "all" ? fixtures : fixtures.filter(f => f.forecast === filter);
  const counts = {
    Draw: fixtures.filter(f => f.forecast === "Draw").length,
    "Home Win": fixtures.filter(f => f.forecast === "Home Win").length,
    "Away Win": fixtures.filter(f => f.forecast === "Away Win").length,
  };
  const coupon = data?.coupon_color || "blue";
  const couponBg = COUPON_BG[coupon] || COUPON_BG.blue;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Header />
      <main className="max-w-6xl mx-auto px-4 pt-28 pb-10">
        <AdvertBanner />
        {/* Hero */}
        <div className="text-center mb-8">
          <Badge className="bg-emerald-600 text-white mb-3 inline-flex items-center gap-1.5"><Target className="h-3 w-3" /> POOL FIXTURES</Badge>
          <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900">
            Week {data?.week_number ?? "—"} Coupon Forecast
          </h1>
          <div className="flex items-center justify-center gap-3 mt-3 flex-wrap">
            {coupon && (
              <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase text-white ${couponBg}`} data-testid="coupon-color-badge">
                <Trophy className="h-3.5 w-3.5" /> {coupon} Coupon
              </span>
            )}
            <span className="text-xs text-gray-500">{data?.season || "UK"} season · {fixtures.length} fixtures</span>
            {data?.hit_rate_pct !== null && data?.hit_rate_pct !== undefined && (
              <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-3 py-1 rounded-full inline-flex items-center gap-1" data-testid="hit-rate-badge">
                <TrendingUp className="h-3 w-3" /> Historical hit rate: {data.hit_rate_pct}% ({data.settled_picks} settled picks)
              </span>
            )}
          </div>
          <p className="text-gray-500 mt-3 text-sm max-w-2xl mx-auto">
            All {fixtures.length} fixtures from this week&apos;s coupon with per-game forecast (Draw / Home Win / Away Win probabilities) derived from past results.
          </p>
        </div>

        {/* Filter tabs */}
        {fixtures.length > 0 && (
          <div className="flex items-center justify-center gap-2 flex-wrap mb-6" data-testid="forecast-filter">
            {[
              { key: "all", label: `All (${fixtures.length})`, color: "bg-gray-900" },
              { key: "Draw", label: `Draws (${counts.Draw})`, color: "bg-emerald-600" },
              { key: "Home Win", label: `Home Wins (${counts["Home Win"]})`, color: "bg-blue-600" },
              { key: "Away Win", label: `Away Wins (${counts["Away Win"]})`, color: "bg-indigo-600" },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide transition-all ${filter === f.key ? `${f.color} text-white shadow` : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300"}`}
                data-testid={`filter-${f.key.replace(" ", "-").toLowerCase()}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="text-center text-gray-400 py-12">Loading fixtures…</p>
        ) : filtered.length === 0 ? (
          <Card className="text-center py-12 border-dashed">
            <CardContent>
              <Target className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">
                {fixtures.length === 0
                  ? "No pool fixtures generated yet. Admin can generate the coupon from the dashboard."
                  : `No fixtures match the "${filter}" forecast for this week.`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="fixtures-grid">
            {filtered.map(f => {
              const forecastBg =
                f.forecast === "Draw" ? "bg-emerald-100 text-emerald-700" :
                f.forecast === "Home Win" ? "bg-blue-100 text-blue-700" :
                "bg-indigo-100 text-indigo-700";
              return (
                <Card key={f.game_id} className="border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all" data-testid={`fixture-${f.coupon_number}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                      <Badge className="bg-gray-900 text-white text-[10px]">Coupon #{f.coupon_number}</Badge>
                      <Badge className={`${forecastBg} text-[10px] font-bold`} data-testid={`fixture-forecast-${f.coupon_number}`}>
                        {f.forecast} · {f.forecast_pct}%
                      </Badge>
                    </div>
                    <p className="font-bold text-gray-900 leading-tight">{f.home_team} vs {f.away_team}</p>
                    <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
                      <span>{f.league}</span>
                      {f.kickoff && (
                        <span className="inline-flex items-center gap-1 text-amber-600"><Clock className="h-3 w-3" />{f.kickoff}</span>
                      )}
                    </div>
                    {/* Probability bars */}
                    <div className="mt-3 space-y-1.5">
                      <ProbBar label="Draw" pct={f.draw_pct} barClass="bg-emerald-500" />
                      <ProbBar label="Home" pct={f.home_win_pct} barClass="bg-blue-500" />
                      <ProbBar label="Away" pct={f.away_win_pct} barClass="bg-indigo-500" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

const ProbBar = ({ label, pct, barClass }) => (
  <div className="flex items-center gap-2">
    <span className="text-[10px] font-bold text-gray-500 w-10">{label}</span>
    <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
      <div className={`${barClass} h-full`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
    </div>
    <span className="text-[10px] font-bold text-gray-700 w-9 text-right">{pct}%</span>
  </div>
);

export default PoolFixturesPage;
